/**
 * A reference of all countries' ISO 3166-1 Alpha 2 and
 * Alpha 3 country codes, and helpful functions to convert between.
 */
import isoCountries from "i18n-iso-countries/codes.json";

export type IsoCountry = [string, string];

declare type isoCountries = IsoCountry[];

export interface IsoMap {
  [isoKey: string]: string;
}

//@TODO do we need both codes?
const COUNTRIES_POSTCODE_NOT_REQUIRED = [
  "AO",
  "AG",
  "AW",
  "BS",
  "BZ",
  "BJ",
  "BW",
  "BF",
  "BI",
  "CM",
  "CF",
  "KM",
  "CG",
  "CD",
  "CK",
  "CI",
  "DJ",
  "DM",
  "GQ",
  "ER",
  "FJ",
  "TF",
  "GM",
  "GH",
  "GD",
  "GN",
  "GY",
  "HK",
  "IE",
  "JM",
  "KE",
  "KI",
  "MO",
  "MW",
  "ML",
  "MR",
  "MU",
  "MS",
  "NR",
  "AN",
  "NU",
  "KP",
  "PA",
  "QA",
  "RW",
  "KN",
  "LC",
  "ST",
  "SC",
  "SL",
  "SB",
  "SO",
  "ZA",
  "SR",
  "SY",
  "TZ",
  "TL",
  "TK",
  "TO",
  "TT",
  "TV",
  "UG",
  "AE",
  "VU",
  "YE",
  "ZW"
];

/**
 * Map of ISO 3166-1 (alpha 2) country codes to ISO 3166-1 (alpha 3).
 */
export const ISO2_TO_ISO3: IsoMap = isoCountries.reduce(
  (acc: IsoMap, [iso2, iso3]) => {
    acc[iso2.toUpperCase()] = iso3.toUpperCase();
    return acc;
  },
  {}
);

/**
 * Map of ISO 3166-1 (alpha 3) country codes to ISO 3166-1 (alpha 2)
 */
export const ISO3_TO_ISO2: IsoMap = isoCountries.reduce(
  (acc: IsoMap, [iso2, iso3]) => {
    acc[iso3.toUpperCase()] = iso2.toUpperCase();
    return acc;
  },
  {}
);

/**
 * Check if the input is a valid ISO 3166-1 (alpha 2) or
 * ISO 3166-1 (alpha 3) code.
 */
export function isValidIso(iso: string) {
  const findIso = iso.toUpperCase();
  return (
    !!findIso &&
    (ISO2_TO_ISO3.hasOwnProperty(findIso) ||
      ISO3_TO_ISO2.hasOwnProperty(findIso))
  );
}

/**
 * Convert ISO 3166-1 (alpha 3) to ISO 3166-1 (alpha 2).
 */
export function getIso2(iso3: string) {
  let findByIso3 = iso3.toUpperCase();
  return ISO3_TO_ISO2.hasOwnProperty(findByIso3)
    ? ISO3_TO_ISO2[findByIso3]
    : undefined;
}

/**
 * Convert ISO 3166-1 (alpha 2) to ISO 3166-1 (alpha 3).
 */
export function getIso3(iso2: string) {
  let findByIso2 = iso2.toUpperCase();
  return ISO2_TO_ISO3.hasOwnProperty(findByIso2)
    ? ISO2_TO_ISO3[findByIso2]
    : undefined;
}

/**
 * Checks if the country requires a post code for address validation.
 */
export function isPostCodeRequired(iso2OrIso3: string): boolean {
  const _iso = iso2OrIso3.toUpperCase();
  return !COUNTRIES_POSTCODE_NOT_REQUIRED.includes(_iso);
}
